import React, { useState, useMemo } from 'react';
import { Trash, Edit, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'react-hot-toast';

const AppsTable = ({ apps, onEdit, onRefresh }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const paginatedApps = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return apps.slice(start, start + itemsPerPage);
  }, [apps, currentPage]);

  const totalPages = Math.ceil(apps.length / itemsPerPage);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this app?')) return;
    try {
      const res = await fetch(`http://localhost:5003/admin/apps/${id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('App deleted');
        onRefresh();
      } else {
        toast.error('Failed to delete app');
      }
    } catch {
      toast.error('Server error');
    }
  };

  const toggleStatus = async (app) => {
    try {
      const res = await fetch(`http://localhost:5003/admin/apps/${app._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...app, enabled: !app.enabled })
      });
      if (res.ok) {
        toast.success(`App ${app.enabled ? 'disabled' : 'enabled'}`);
        onRefresh();
      } else {
        toast.error('Failed to update app');
      }
    } catch {
      toast.error('Server error');
    }
  };

  return (
    <div className="space-y-4">
      <div className="overflow-auto border rounded-md">
        <table className="min-w-full text-sm bg-white">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">Route</th>
              <th className="text-left px-4 py-2">Tags</th>
              <th className="text-left px-4 py-2">Countries</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Created</th>
              <th className="text-left px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedApps.map(app => (
              <tr key={app._id} className="border-t">
                <td className="px-4 py-2">{app.name}</td>
                <td className="px-4 py-2">{app.routeKey}</td>
                <td className="px-4 py-2">{app.tags?.join(', ')}</td>
                <td className="px-4 py-2">{app.countries?.join(', ')}</td>
                <td className="px-4 py-2">
                  {app.enabled ? (
                    <span className="text-green-600">Enabled</span>
                  ) : (
                    <span className="text-red-500">Disabled</span>
                  )}
                </td>
                <td className="px-4 py-2">{new Date(app.createdAt).toLocaleDateString()}</td>
                <td className="px-4 py-2 flex gap-2">
                  <button onClick={() => onEdit(app)} className="text-blue-600 hover:underline text-xs">
                    <Edit size={14} />
                  </button>
                  <button onClick={() => handleDelete(app._id)} className="text-red-600 hover:underline text-xs">
                    <Trash size={14} />
                  </button>
                  <button onClick={() => toggleStatus(app)} className="text-gray-600 hover:underline text-xs">
                    {app.enabled ? <ToggleLeft size={16} /> : <ToggleRight size={16} />}
                  </button>
                </td>
              </tr>
            ))}
            {paginatedApps.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center text-gray-500 py-4">No apps found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-end gap-2 mt-2">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`px-3 py-1 rounded border ${currentPage === i + 1 ? 'bg-blue-600 text-white' : 'bg-white'}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppsTable;
